import { useState, FormEvent } from 'react';
import { useCatalog } from '../context/CatalogContext';

interface Props {
  onClose: () => void;
}

export function SuggestToolModal({ onClose }: Props) {
  const { allTools } = useCatalog();
  const [name, setName] = useState('');
  const [website, setWebsite] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  // "See if our directory has it" — live match against the current catalog.
  const trimmed = name.trim().toLowerCase();
  const matches =
    trimmed.length >= 2
      ? allTools.filter(t => t.name.toLowerCase().includes(trimmed)).slice(0, 3)
      : [];

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim() || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch('/api/v1/tools?suggest', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          website: website.trim() || undefined,
          notes: notes.trim() || undefined,
        }),
      });
      if (res.status === 429) {
        setError('Too many suggestions right now — please try again in a minute.');
        return;
      }
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        setError(body?.error?.message ?? 'Something went wrong. Please try again.');
        return;
      }
      setDone(true);
    } catch {
      setError('Network error — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="bg-white rounded-card shadow-modal w-full max-w-md mx-4 p-6"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Suggest a tool"
      >
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-lg font-semibold text-neutral-800 font-heading">Suggest A Tool</h2>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600" aria-label="Close">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {done ? (
          <div className="py-6 text-center">
            <div className="w-12 h-12 rounded-full bg-emerald-50 text-emerald-600 mx-auto flex items-center justify-center mb-3">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-base font-semibold text-neutral-800 font-heading mb-1">Thanks for the suggestion!</h3>
            <p className="text-sm text-neutral-500">
              Our team reviews every submission and vets tools for HIPAA compliance before adding them.
            </p>
            <button onClick={onClose} className="btn-primary mt-4">Done</button>
          </div>
        ) : (
          <>
            <p className="text-sm text-neutral-500 mb-4">
              Know a healthcare AI tool we're missing? Tell us and we'll vet it for the directory.
            </p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-neutral-600 mb-1">
                  Tool Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="e.g. Nabla"
                  className="input-base"
                  required
                  maxLength={120}
                  autoFocus
                />
                {matches.length > 0 && (
                  <div className="mt-2 p-2 bg-primary-50 border border-primary-100 rounded-btn">
                    <p className="text-xs font-medium text-primary-700 mb-1">Already in the directory:</p>
                    {matches.map(t => (
                      <p key={t.id} className="text-xs text-primary-600">
                        • {t.name} <span className="text-primary-400">({t.hipaaCompliant ? 'HIPAA verified' : 'not HIPAA verified'})</span>
                      </p>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-neutral-600 mb-1">Website (optional)</label>
                <input
                  type="url"
                  value={website}
                  onChange={e => setWebsite(e.target.value)}
                  placeholder="https://..."
                  className="input-base"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-neutral-600 mb-1">
                  What would you use it for? (optional)
                </label>
                <textarea
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  placeholder="e.g. Prior auth letters for orthopedics..."
                  rows={2}
                  className="input-base resize-none"
                  maxLength={1000}
                />
              </div>
              {error && <p className="text-xs text-red-600">{error}</p>}
              <div className="flex justify-end gap-2">
                <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
                <button type="submit" disabled={submitting || !name.trim()} className="btn-primary disabled:opacity-50">
                  {submitting ? 'Sending...' : 'Submit Suggestion'}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
